import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Save,
  User,
  Users,
  Monitor,
  Package,
  Shield,
  Layers,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Tag,
  Rocket,
  RefreshCw,
  Plus,
  X,
  Mail,
  ExternalLink,
  Ban,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Image as ImageIcon,
  ClipboardList,
  GitBranch,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePermissions } from "@/contexts/PermissionsContext";
import PipelineStagesEditor, {
  PipelineStagesSummary,
  STAGE_GROUP,
  STAGE_UAT,
  cleanStagesForSave,
  copyStages,
  firstPipelineError,
} from "@/components/deployment/PipelineStagesEditor";
import FamilyDependenciesCard from "@/components/FamilyDependenciesCard";
import FamilyRationalisationCard from "@/components/FamilyRationalisationCard";

/**
 * One row in the Test Approvals card, collapsible to reveal the evidence
 * (comments + structured test steps + screenshot thumbnails) captured via
 * the tester evidence form.
 */
const ApprovalRow = ({ approval, idx }) => {
  const [open, setOpen] = useState(false);
  const evidence = approval?.evidence || {};
  const hasComments = Boolean((evidence.comments || "").trim());
  const hasSteps = Array.isArray(evidence.test_steps) && evidence.test_steps.length > 0;
  const hasImages = Array.isArray(evidence.images) && evidence.images.length > 0;
  const hasAnyEvidence = hasComments || hasSteps || hasImages;

  const statusStyles =
    approval.status === "approved"
      ? "bg-green-400"
      : approval.status === "rejected"
      ? "bg-red-400"
      : "bg-orange-400";

  const statusBadge =
    approval.status === "approved" ? (
      <Badge className="bg-green-600/20 text-green-400 text-xs">Approved</Badge>
    ) : approval.status === "rejected" ? (
      <Badge className="bg-red-600/20 text-red-400 text-xs">Rejected</Badge>
    ) : (
      <Badge className="bg-orange-600/20 text-orange-400 text-xs">Pending</Badge>
    );

  return (
    <div
      className="border-b border-[rgba(229,239,250,0.1)] last:border-0"
      data-testid={`approval-row-${idx}`}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between py-2 gap-2 text-left"
        onClick={() => hasAnyEvidence && setOpen((v) => !v)}
        aria-expanded={open}
        disabled={!hasAnyEvidence}
        data-testid={`approval-row-toggle-${idx}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full ${statusStyles}`} />
          <span className="text-sm text-white/90 truncate">{approval.tester_email}</span>
          {hasAnyEvidence && (
            <span className="flex items-center gap-1 text-[11px] text-white/50 ml-1">
              {hasComments && <MessageSquare className="w-3 h-3" />}
              {hasSteps && <ClipboardList className="w-3 h-3" />}
              {hasImages && <ImageIcon className="w-3 h-3" />}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {statusBadge}
          {hasAnyEvidence ? (
            open ? (
              <ChevronDown className="w-4 h-4 text-white/60" />
            ) : (
              <ChevronRight className="w-4 h-4 text-white/60" />
            )
          ) : null}
        </div>
      </button>

      {open && hasAnyEvidence && (
        <div
          className="pb-3 pl-4 pr-2 space-y-3 text-sm"
          data-testid={`approval-row-evidence-${idx}`}
        >
          {approval.responded_at && (
            <p className="text-white/50 text-xs">
              Responded {new Date(approval.responded_at).toLocaleString()}
            </p>
          )}
          {hasComments && (
            <div>
              <p className="text-white/80 font-medium text-xs mb-1 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" /> Comments
              </p>
              <div className="whitespace-pre-wrap text-white/90 bg-[rgba(229,239,250,0.08)] rounded p-2 text-xs">
                {evidence.comments}
              </div>
            </div>
          )}
          {hasSteps && (
            <div>
              <p className="text-white/80 font-medium text-xs mb-1 flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5" /> Test steps ({evidence.test_steps.length})
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-white/60">
                      <th className="text-left font-medium px-1.5 py-1 w-6">#</th>
                      <th className="text-left font-medium px-1.5 py-1">Action</th>
                      <th className="text-left font-medium px-1.5 py-1">Expected</th>
                      <th className="text-left font-medium px-1.5 py-1">Actual</th>
                      <th className="text-left font-medium px-1.5 py-1 w-20">Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evidence.test_steps.map((s, i) => (
                      <tr key={i} className="border-t border-[rgba(229,239,250,0.08)] align-top">
                        <td className="px-1.5 py-1 text-white/60">{i + 1}</td>
                        <td className="px-1.5 py-1 text-white/90">{s.action || "—"}</td>
                        <td className="px-1.5 py-1 text-white/90">{s.expected || "—"}</td>
                        <td className="px-1.5 py-1 text-white/90">{s.actual || "—"}</td>
                        <td className="px-1.5 py-1">
                          {s.outcome ? (
                            <span
                              className={`capitalize ${
                                s.outcome === "pass"
                                  ? "text-green-400"
                                  : s.outcome === "fail"
                                  ? "text-red-400"
                                  : "text-amber-400"
                              }`}
                            >
                              {s.outcome}
                            </span>
                          ) : (
                            <span className="text-white/40">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {hasImages && (
            <div>
              <p className="text-white/80 font-medium text-xs mb-1 flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5" /> Screenshots ({evidence.images.length})
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {evidence.images.map((img, i) => (
                  <a
                    key={i}
                    href={img.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block border border-[rgba(229,239,250,0.15)] rounded overflow-hidden bg-[rgba(229,239,250,0.05)] p-1"
                    title={img.blob_name}
                  >
                    <img
                      src={img.url}
                      alt={`screenshot ${i + 1}`}
                      loading="lazy"
                      className="w-full h-20 object-cover rounded-sm"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentElement.innerHTML =
                          '<div class="w-full h-20 flex items-center justify-center text-[10px] text-white/50">Image (auth required)</div>';
                      }}
                    />
                    <div className="text-[10px] text-white/50 mt-1 truncate">
                      {(img.size_bytes || 0) >= 1024
                        ? `${Math.round((img.size_bytes || 0) / 1024)} KB`
                        : `${img.size_bytes || 0} B`}
                    </div>
                  </a>
                ))}
              </div>
              <p className="text-[10px] text-white/40 mt-1">
                Links require your Azure Blob Storage access.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AppGovernancePage = () => {
  const { familyName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAccessToken } = useAuth();
  // Changing the deployment pipeline needs deploy_packages on the server;
  // without it the pipeline card is shown read-only.
  const { canDeployPackages } = usePermissions();

  // Get the source view to return to (default to rationalisation since that's where governance links are)
  const sourceView = searchParams.get("from") || "rationalisation";
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [appData, setAppData] = useState(null);
  const [testApprovals, setTestApprovals] = useState(null);
  
  // Governance form state
  const [owner, setOwner] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [testers, setTesters] = useState([]);
  const [newTester, setNewTester] = useState("");
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [riskLevel, setRiskLevel] = useState(null); // green, amber, red
  const [excludedVersions, setExcludedVersions] = useState([]); // Versions to exclude from upgrades
  // Deployment pipeline (governance.deployment_pipeline). Only sent on save
  // when pipelineDirty, so editing the owner or tags never rewrites it.
  const [pipelineMode, setPipelineMode] = useState("default"); // default | template | custom
  const [pipelineTemplateId, setPipelineTemplateId] = useState("");
  const [pipelineStages, setPipelineStages] = useState([]);
  const [pipelineDirty, setPipelineDirty] = useState(false);
  const [legacyAdopted, setLegacyAdopted] = useState(false);
  // Tenant templates and default (GET /deployment-automation/settings) and
  // what the next rollout will actually use (GET /deployment-pipeline/resolve).
  const [pipelineSettings, setPipelineSettings] = useState(null);
  const [resolvedPipeline, setResolvedPipeline] = useState(null);
  const [expandedCveVersions, setExpandedCveVersions] = useState({}); // { [version]: bool } — CVE detail toggle

  const fetchAppData = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${API}/discover/governance/${encodeURIComponent(familyName)}`,
        { headers }
      );
      
      setAppData(response.data);
      
      // Populate form with existing governance data
      const gov = response.data.governance || {};
      setOwner(gov.owner || "");
      setOwnerEmail(gov.owner_email || "");
      setTesters(gov.testers || []);
      setNotes(gov.notes || "");
      setTags(gov.tags || []);
      setRiskLevel(gov.risk_level || null);
      setExcludedVersions(gov.excluded_versions || []);
      const familyPipeline = gov.deployment_pipeline || {};
      setPipelineMode(familyPipeline.mode || "default");
      setPipelineTemplateId(familyPipeline.template_id || "");
      setPipelineStages(familyPipeline.mode === "custom" ? copyStages(familyPipeline.stages) : []);
      setPipelineDirty(false);
      setLegacyAdopted(false);

      // Fetch test approvals if there's a package
      const packageId = response.data.packaging_status?.package_id;
      if (packageId) {
        try {
          const approvalsResponse = await axios.get(
            `${API}/packages/${packageId}/test-approvals`,
            { headers }
          );
          setTestApprovals(approvalsResponse.data);
        } catch (e) {
          console.log("Could not fetch test approvals");
        }
      }
      
    } catch (error) {
      console.error("Error fetching app data:", error);
      if (error.response?.status === 404) {
        toast.error("App family not found");
        navigate(`/discover?view=${sourceView}`);
      } else {
        toast.error("Failed to load app data");
      }
    } finally {
      setLoading(false);
    }
  }, [familyName, getAccessToken, navigate, sourceView]);

  // Tenant pipeline templates and the stages this family's next rollout will
  // use. Each call fails on its own: a missing preview must not hide the page.
  const fetchPipelineInfo = useCallback(async () => {
    let headers = {};
    try {
      const token = await getAccessToken();
      headers = token ? { Authorization: `Bearer ${token}` } : {};
    } catch (e) {
      headers = {};
    }
    const [settingsResult, resolveResult] = await Promise.allSettled([
      axios.get(`${API}/deployment-automation/settings`, { headers }),
      axios.get(`${API}/deployment-pipeline/resolve`, {
        headers,
        params: { family_name: familyName },
      }),
    ]);
    if (settingsResult.status === "fulfilled") {
      setPipelineSettings(settingsResult.value.data);
    } else {
      console.error("Could not load deployment pipeline templates:", settingsResult.reason);
    }
    if (resolveResult.status === "fulfilled") {
      setResolvedPipeline(resolveResult.value.data);
    } else {
      console.error("Could not resolve the deployment pipeline:", resolveResult.reason);
      setResolvedPipeline(null);
    }
  }, [familyName, getAccessToken]);

  useEffect(() => {
    if (familyName) {
      fetchAppData();
      fetchPipelineInfo();
    }
  }, [familyName, fetchAppData, fetchPipelineInfo]);

  const pipelineTemplates = Array.isArray(pipelineSettings?.stage_templates)
    ? pipelineSettings.stage_templates
    : [];
  const defaultPipelineTemplate =
    pipelineTemplates.find((t) => t.id === pipelineSettings?.default_stage_template_id) ||
    pipelineTemplates[0] ||
    null;
  const selectedPipelineTemplate = pipelineTemplates.find((t) => t.id === pipelineTemplateId) || null;
  const pipelineThresholdDefaults = pipelineSettings
    ? {
        first: pipelineSettings.phase1_to_phase2_threshold,
        later: pipelineSettings.phase2_to_phase3_threshold,
      }
    : null;

  // The July 2026 "Use pre-defined Entra ID group" switch. Its backend was
  // removed by mistake in August 2026, so where it is still set it has been
  // showing "on" while rollouts used the normal pipeline. Nothing applies it
  // automatically; the owner is offered it as a custom pipeline to confirm.
  const legacyGovernance = appData?.governance || {};
  const legacyGroupName =
    !legacyGovernance.deployment_pipeline &&
    legacyGovernance.use_predefined_group &&
    (legacyGovernance.predefined_group_name || "").trim()
      ? legacyGovernance.predefined_group_name.trim()
      : null;

  const choosePipelineMode = (mode) => {
    if (mode === pipelineMode) return;
    setPipelineMode(mode);
    setPipelineDirty(true);
    if (mode === "template" && !pipelineTemplateId) {
      // Preselect the first template the tenant made itself, else the default.
      const firstCustom = pipelineTemplates.find((t) => !t.built_in);
      setPipelineTemplateId((firstCustom || defaultPipelineTemplate)?.id || "");
    }
    if (mode === "custom" && pipelineStages.length === 0) {
      // Seed from what the app uses today, so Custom starts as a tweak.
      setPipelineStages(copyStages(resolvedPipeline?.stages || defaultPipelineTemplate?.stages || []));
    }
  };

  const adoptLegacyGroupAsPipeline = () => {
    if (!legacyGroupName) return;
    const groupStage = {
      id: "predefined",
      type: STAGE_GROUP,
      name: legacyGroupName,
      group_name: legacyGroupName,
    };
    if (legacyGovernance.predefined_group_id) groupStage.group_id = legacyGovernance.predefined_group_id;
    setPipelineStages([{ id: "uat", type: STAGE_UAT, name: "UAT" }, groupStage]);
    setPipelineMode("custom");
    setPipelineDirty(true);
    setLegacyAdopted(true);
  };

  // The stages the next rollout would use if the pending choice were saved.
  const pendingPipelineStages =
    pipelineMode === "custom"
      ? pipelineStages
      : pipelineMode === "template"
      ? selectedPipelineTemplate?.stages || []
      : defaultPipelineTemplate?.stages || [];

  const resolvedSourceLabel = (() => {
    if (!resolvedPipeline) return null;
    if (resolvedPipeline.source === "custom") return "Custom pipeline for this app";
    if (resolvedPipeline.source === "template") return `Template: ${resolvedPipeline.template_name}`;
    return `Tenant default: ${resolvedPipeline.template_name}`;
  })();

  const handleSave = async () => {
    let deploymentPipeline = null;
    if (pipelineDirty && canDeployPackages) {
      if (pipelineMode === "template") {
        if (!pipelineTemplateId) {
          toast.error("Choose a template for the deployment pipeline");
          return;
        }
        deploymentPipeline = { mode: "template", template_id: pipelineTemplateId };
      } else if (pipelineMode === "custom") {
        const problem = firstPipelineError(pipelineStages);
        if (problem) {
          toast.error(`Deployment pipeline: ${problem}`);
          return;
        }
        deploymentPipeline = { mode: "custom", stages: cleanStagesForSave(pipelineStages) };
      } else {
        deploymentPipeline = { mode: "default" };
      }
    }
    setSaving(true);
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(
        `${API}/discover/governance/${encodeURIComponent(familyName)}`,
        { 
          owner, 
          owner_email: ownerEmail, 
          testers, 
          notes, 
          tags, 
          risk_level: riskLevel,
          excluded_versions: excludedVersions,
          // Omitted unless changed: the server then leaves it as it is.
          ...(deploymentPipeline ? { deployment_pipeline: deploymentPipeline } : {}),
        },
        { headers }
      );

      toast.success("Governance settings saved");
      fetchAppData();
      fetchPipelineInfo();
    } catch (error) {
      console.error("Error saving governance:", error);
      toast.error(error.response?.data?.detail || "Failed to save governance settings");
    } finally {
      setSaving(false);
    }
  };

  const addTester = () => {
    if (newTester.trim() && !testers.includes(newTester.trim())) {
      setTesters([...testers, newTester.trim()]);
      setNewTester("");
    }
  };

  const removeTester = (tester) => {
    setTesters(testers.filter(t => t !== tester));
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const removeTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleResendNotifications = async () => {
    const packageId = appData?.packaging_status?.package_id;
    if (!packageId) {
      toast.error("No package ID found");
      return;
    }
    
    setSendingNotifications(true);
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/packages/${packageId}/resend-tester-notifications`,
        {},
        { headers }
      );
      
      toast.success(response.data.message || "Tester notifications sent!");
      
      // Refresh data to show new approvals
      fetchAppData();
    } catch (error) {
      console.error("Error sending notifications:", error);
      toast.error(error.response?.data?.detail || "Failed to send tester notifications");
    } finally {
      setSendingNotifications(false);
    }
  };

  const getStatusBadge = () => {
    const status = appData?.packaging_status?.status;
    const allApproved = appData?.packaging_status?.all_testers_approved;
    
    if (status === "live") {
      return <Badge className="bg-purple-600 text-white">LIVE</Badge>;
    } else if (status === "approved" || allApproved) {
      return <Badge className="bg-green-600 text-white animate-pulse">✓ Ready for Go Live</Badge>;
    } else if (status === "deployed_test") {
      return <Badge className="bg-orange-600 text-white">Deployed to Test</Badge>;
    } else if (status === "packaged") {
      return <Badge className="bg-blue-600 text-white">Packaged</Badge>;
    }
    return <Badge variant="outline" className="text-white/90">Not Packaged</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  if (!appData) {
    return (
      <div className="min-h-screen bg-transparent p-6">
        <div className="text-center py-12">
          <p className="text-white/90">App not found</p>
          <Button variant="outline" onClick={() => navigate(`/discover?view=${sourceView}`, { state: { syncOnMount: true } })} className="mt-4 bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold" data-testid="back-to-discover-not-found-btn">
            Back to Discover
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/discover?view=${sourceView}`, { state: { syncOnMount: true } })}
              className="text-white/90 hover:text-white"
              data-testid="back-to-discover-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Discover
            </Button>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>

        {/* App Header Card */}
        <Card className="mb-6 bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardContent className="py-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">{appData.family_name}</h1>
                <p className="text-white/90 mb-3">{appData.publisher || "Unknown Publisher"}</p>
                <div className="flex items-center gap-3">
                  {getStatusBadge()}
                  {appData.winget_data?.matched && (
                    <Badge className="bg-green-600/20 text-green-400 border border-green-500">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Winget: {appData.winget_data.package_identifier}
                    </Badge>
                  )}
                  {appData.cve_data?.has_cves && (
                    <Badge className="bg-red-600/20 text-red-400 border border-red-500">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {appData.cve_data.cve_count} CVEs
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-white">{appData.unique_device_count?.toLocaleString() || 0}</div>
                <div className="text-sm text-white/90">Devices</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Governance Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Owner & Testers */}
            <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Ownership & Testing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/90">App Owner</Label>
                    <Input
                      value={owner}
                      onChange={(e) => setOwner(e.target.value)}
                      placeholder="Enter owner name"
                      className="mt-1 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-white/15 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white/90">Owner Email</Label>
                    <Input
                      type="email"
                      value={ownerEmail}
                      onChange={(e) => setOwnerEmail(e.target.value)}
                      placeholder="owner@company.com"
                      className="mt-1 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-white/15 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white/90">Testers</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={newTester}
                      onChange={(e) => setNewTester(e.target.value)}
                      placeholder="Add tester email"
                      className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-white/15 text-white"
                      onKeyDown={(e) => e.key === "Enter" && addTester()}
                    />
                    <Button onClick={addTester} variant="outline" size="sm" className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {testers.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {testers.map((tester, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white pl-2 pr-1">
                          {tester}
                          <button onClick={() => removeTester(tester)} className="ml-1 hover:text-red-400">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-white/90">Business Criticality</Label>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setRiskLevel("green")}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                        riskLevel === "green"
                          ? "bg-green-500/20 border-green-500 text-green-400"
                          : "bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-white/15 text-white/90 hover:border-green-500/50"
                      }`}
                      data-testid="risk-level-green"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500" />
                        <span className="font-medium">Low</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRiskLevel("amber")}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                        riskLevel === "amber"
                          ? "bg-amber-500/20 border-amber-500 text-amber-400"
                          : "bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-white/15 text-white/90 hover:border-amber-500/50"
                      }`}
                      data-testid="risk-level-amber"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-amber-500" />
                        <span className="font-medium">Medium</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRiskLevel("red")}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                        riskLevel === "red"
                          ? "bg-red-500/20 border-red-500 text-red-400"
                          : "bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-white/15 text-white/90 hover:border-red-500/50"
                      }`}
                      data-testid="risk-level-red"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-red-500" />
                        <span className="font-medium">High</span>
                      </div>
                    </button>
                  </div>
                  {riskLevel && (
                    <button
                      onClick={() => setRiskLevel(null)}
                      className="text-xs text-white/80 hover:text-white/90 mt-2"
                    >
                      Clear criticality
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notes & Tags */}
            <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Notes & Tags
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-white/90">Notes</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this application..."
                    className="mt-1 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-white/15 text-white min-h-[100px]"
                  />
                </div>

                <div>
                  <Label className="text-white/90">Tags</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add tag"
                      className="bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-white/15 text-white"
                      onKeyDown={(e) => e.key === "Enter" && addTag()}
                    />
                    <Button onClick={addTag} variant="outline" size="sm" className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {tags.map((tag, idx) => (
                        <Badge key={idx} className="bg-blue-600/20 text-blue-400 border border-blue-500 pl-2 pr-1">
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                          <button onClick={() => removeTag(tag)} className="ml-1 hover:text-red-400">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Deployment pipeline: the stages this app's next rollout uses.
                Replaces the July 2026 "Deployment Target" switch. */}
            <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]" data-testid="deployment-pipeline-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <GitBranch className="h-5 w-5" />
                  Deployment pipeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-white/60">
                  The stages a new version of this app passes through after packaging. Changes apply
                  to the next rollout; a rollout already under way keeps its stages.
                </p>

                {legacyGroupName && !legacyAdopted && (
                  <div
                    className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 space-y-2"
                    data-testid="legacy-predefined-group-notice"
                  >
                    <div className="flex items-start gap-2 text-sm text-amber-300">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        This app has the old <strong>Use pre-defined Entra ID group</strong> switch
                        turned on for <strong>{legacyGroupName}</strong>. That switch has not been
                        applied to rollouts since August 2026, so this app has been using the
                        pipeline shown below instead.
                      </span>
                    </div>
                    {canDeployPackages ? (
                      <div className="flex flex-wrap items-center gap-2 pl-6">
                        <Button
                          type="button"
                          size="sm"
                          onClick={adoptLegacyGroupAsPipeline}
                          className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                          data-testid="legacy-use-as-custom-btn"
                        >
                          Use as custom pipeline
                        </Button>
                        <span className="text-xs text-white/70">
                          Sets up UAT then {legacyGroupName} for you to check and save. Saving any
                          pipeline choice clears the old switch.
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs text-white/70 pl-6">
                        Someone with permission to deploy packages can turn it into a custom pipeline.
                      </p>
                    )}
                  </div>
                )}
                {legacyAdopted && (
                  <p className="text-xs text-amber-300" data-testid="legacy-adopted-note">
                    Custom pipeline set up from the old {legacyGroupName || "pre-defined group"} switch.
                    Check the stages, then click Save Changes.
                  </p>
                )}

                {canDeployPackages && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Deployment pipeline">
                      {[
                        {
                          mode: "default",
                          label: "Tenant default",
                          detail: defaultPipelineTemplate ? defaultPipelineTemplate.name : "Set in Settings",
                        },
                        { mode: "template", label: "A template", detail: "Choose one of the tenant's templates" },
                        { mode: "custom", label: "Custom for this app", detail: "Stages for this app only" },
                      ].map((option) => {
                        const selected = pipelineMode === option.mode;
                        return (
                          <button
                            key={option.mode}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => choosePipelineMode(option.mode)}
                            className={`text-left py-2 px-3 rounded-lg border-2 transition-all ${
                              selected
                                ? "bg-blue-500/20 border-blue-400 text-white"
                                : "bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-white/15 text-white/90 hover:border-blue-400/50"
                            }`}
                            data-testid={`pipeline-mode-${option.mode}`}
                          >
                            <div className="text-sm font-medium">{option.label}</div>
                            <div className="text-xs text-white/70 mt-0.5 truncate">{option.detail}</div>
                          </button>
                        );
                      })}
                    </div>

                    {pipelineMode === "template" && (
                      <div className="max-w-sm">
                        <Label className="text-white/90">Template</Label>
                        <Select
                          value={pipelineTemplateId || ""}
                          onValueChange={(value) => {
                            setPipelineTemplateId(value);
                            setPipelineDirty(true);
                          }}
                        >
                          <SelectTrigger className="mt-1" data-testid="pipeline-template-select">
                            <SelectValue placeholder="Choose a template" />
                          </SelectTrigger>
                          <SelectContent>
                            {pipelineTemplates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.name}
                                {t.built_in ? " (built in)" : ""}
                                {t.id === defaultPipelineTemplate?.id ? " - tenant default" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {pipelineTemplateId && !selectedPipelineTemplate && pipelineSettings && (
                          <p className="text-xs text-amber-300 mt-1">
                            The saved template no longer exists. Choose another.
                          </p>
                        )}
                      </div>
                    )}

                    {pipelineMode === "custom" && (
                      <PipelineStagesEditor
                        stages={pipelineStages}
                        onChange={(stages) => {
                          setPipelineStages(stages);
                          setPipelineDirty(true);
                        }}
                        defaultThresholds={pipelineThresholdDefaults}
                        testIdPrefix="governance-pipeline-editor"
                      />
                    )}
                  </div>
                )}

                <div className="space-y-3 rounded-lg bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] p-3">
                  <div>
                    <div className="text-xs text-white/70 mb-1">
                      Next rollout uses{resolvedSourceLabel ? ` (${resolvedSourceLabel})` : ""}
                    </div>
                    {resolvedPipeline ? (
                      <PipelineStagesSummary stages={resolvedPipeline.stages} testId="governance-pipeline-resolved" />
                    ) : (
                      <span className="text-xs text-white/60">Could not load the pipeline preview.</span>
                    )}
                    {resolvedPipeline?.note && (
                      <p className="text-xs text-amber-300 mt-1 flex items-center gap-1" data-testid="governance-pipeline-note">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {resolvedPipeline.note}
                      </p>
                    )}
                  </div>
                  {pipelineDirty && canDeployPackages && (
                    <div>
                      <div className="text-xs text-white/70 mb-1">After you save</div>
                      <PipelineStagesSummary stages={pendingPipelineStages} testId="governance-pipeline-pending" />
                    </div>
                  )}
                </div>

                {!canDeployPackages && (
                  <p className="text-xs text-white/60" data-testid="governance-pipeline-readonly-note">
                    You need permission to deploy packages to change this app's pipeline.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Version Distribution */}
            <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Version Distribution ({appData.version_count} versions)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-300 text-sm">
                    <Ban className="h-4 w-4" />
                    <span>
                      <strong>Exclude from Upgrades:</strong> Toggle off versions where users should NOT receive automatic upgrades. 
                      Devices running excluded versions will be omitted from deployment groups.
                    </span>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-white/90">Version</TableHead>
                      <TableHead className="text-white/90 text-center">CVE Risk</TableHead>
                      <TableHead className="text-white/90 text-right">Devices</TableHead>
                      <TableHead className="text-white/90 text-right">Coverage</TableHead>
                      <TableHead className="text-white/90 text-center">Auto-Upgrade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {appData.versions?.map((version, idx) => {
                      const versionCveData = version.cve_data;
                      const rawSeverity = versionCveData?.highest_severity;
                      // Treat NVD's "NONE" / "UNKNOWN" string (sometimes
                      // persisted in older cve_status rows) as "no
                      // classification" — falls through to the neutral
                      // "Vulnerable" pill rather than rendering literally.
                      const severity = (rawSeverity && !["NONE", "UNKNOWN"].includes(String(rawSeverity).toUpperCase()))
                        ? String(rawSeverity).toUpperCase()
                        : null;
                      const severityConfig = {
                        'CRITICAL': { bg: 'bg-red-600', text: 'text-white' },
                        'HIGH': { bg: 'bg-orange-600', text: 'text-white' },
                        'MEDIUM': { bg: 'bg-yellow-600', text: 'text-white' },
                        'LOW': { bg: 'bg-blue-600', text: 'text-white' }
                      };
                      const config = severity ? severityConfig[severity] : null;
                      const isExcluded = excludedVersions.includes(version.version);
                      const cveList = Array.isArray(versionCveData?.cves) ? versionCveData.cves : [];
                      const hasCveDetail = versionCveData?.matched && cveList.length > 0;
                      const isCveOpen = !!expandedCveVersions[version.version];
                      const toggleCveOpen = () => {
                        setExpandedCveVersions((prev) => ({
                          ...prev,
                          [version.version]: !prev[version.version],
                        }));
                      };
                      
                      const toggleVersionExclusion = (versionStr) => {
                        if (excludedVersions.includes(versionStr)) {
                          setExcludedVersions(excludedVersions.filter(v => v !== versionStr));
                        } else {
                          setExcludedVersions([...excludedVersions, versionStr]);
                        }
                      };
                      
                      return (
                        <React.Fragment key={idx}>
                        <TableRow className={isExcluded ? "opacity-60" : ""}>
                          <TableCell className="text-white">
                            <div className="flex items-center gap-2">
                              {version.version}
                              {idx === 0 && (
                                <Badge className="bg-green-600/20 text-green-400 text-xs">Latest</Badge>
                              )}
                              {isExcluded && (
                                <Badge className="bg-orange-600/20 text-orange-400 text-xs">
                                  <Ban className="h-3 w-3 mr-1" />
                                  Excluded
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {/* Per user direction: never claim a version is "Safe".
                                 - matched=true  → red severity pill (or neutral "Vulnerable" when severity unknown)
                                 - matched=false OR cve_data missing → "—" (no false negatives)
                                The old "Safe" green pill was misleading because a clean
                                evaluator pass doesn't prove safety — it only proves we didn't
                                find a CPE match. Showing "—" forces a conscious read. */}
                            {versionCveData?.matched ? (
                              <div className="flex items-center justify-center gap-1">
                                <Badge className={`${(config && config.bg) || 'bg-red-600'} ${(config && config.text) || 'text-white'} text-xs`}>
                                  {severity || 'Vulnerable'}
                                </Badge>
                                <span className="text-xs text-white/90">({versionCveData.cve_count})</span>
                                {hasCveDetail && (
                                  <button
                                    type="button"
                                    onClick={toggleCveOpen}
                                    className="ml-1 inline-flex items-center rounded p-0.5 hover:bg-white/10 focus:outline-none focus:ring-1 focus:ring-white/40"
                                    aria-label={isCveOpen ? "Collapse CVE list" : "Expand CVE list"}
                                    data-testid={`cve-toggle-${version.version}`}
                                  >
                                    {isCveOpen ? (
                                      <ChevronDown className="h-3.5 w-3.5 text-white/80" />
                                    ) : (
                                      <ChevronRight className="h-3.5 w-3.5 text-white/80" />
                                    )}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-white/40 text-xs" title="No CVE match for this version — could be safe or unmapped. Verify against the Vulnerabilities tab.">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-white">
                            {version.device_count?.toLocaleString() || 0}
                          </TableCell>
                          <TableCell className="text-right text-white/90">
                            {appData.unique_device_count > 0
                              ? Math.round((version.device_count / appData.unique_device_count) * 100)
                              : 0}%
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Switch
                                checked={!isExcluded}
                                onCheckedChange={() => toggleVersionExclusion(version.version)}
                                className="data-[state=checked]:bg-green-600"
                              />
                              <span className={`text-xs ${isExcluded ? 'text-orange-400' : 'text-green-400'}`}>
                                {isExcluded ? 'No' : 'Yes'}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                        {hasCveDetail && isCveOpen && (
                          <TableRow
                            key={`${idx}-cves`}
                            data-testid={`cve-detail-row-${version.version}`}
                            className="bg-white/5"
                          >
                            <TableCell colSpan={5} className="p-0">
                              <div className="px-6 py-4">
                                <div className="text-xs text-white/60 mb-2 uppercase tracking-wide">
                                  CVEs affecting v{version.version}
                                  {versionCveData?.cve_count > cveList.length && (
                                    <span className="ml-2 text-white/40 normal-case">
                                      (showing top {cveList.length} of {versionCveData.cve_count})
                                    </span>
                                  )}
                                </div>
                                <div className="overflow-x-auto rounded border border-white/10">
                                  <table className="w-full text-sm">
                                    <thead className="bg-white/5 text-white/70 text-xs">
                                      <tr>
                                        <th className="text-left px-3 py-2">CVE ID</th>
                                        <th className="text-center px-3 py-2">Severity</th>
                                        <th className="text-right px-3 py-2">CVSS</th>
                                        <th className="text-right px-3 py-2">EPSS</th>
                                        <th className="text-center px-3 py-2">KEV</th>
                                        <th className="text-left px-3 py-2">Fixed In</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {cveList.map((cve, cIdx) => {
                                        const cveSev = (cve.base_severity || "").toUpperCase();
                                        const cveConfig = severityConfig[cveSev];
                                        const epssPct = typeof cve.epss_score === "number"
                                          ? `${(cve.epss_score * 100).toFixed(1)}%`
                                          : "—";
                                        return (
                                          <tr key={cIdx} className="border-t border-white/5">
                                            <td className="px-3 py-2 text-white">
                                              <a
                                                href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(cve.cve_id)}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-300 hover:text-blue-200 hover:underline inline-flex items-center gap-1"
                                                data-testid={`cve-link-${cve.cve_id}`}
                                              >
                                                {cve.cve_id}
                                                <ExternalLink className="h-3 w-3" />
                                              </a>
                                            </td>
                                            <td className="text-center px-3 py-2">
                                              {cveSev && cveSev !== "NONE" && cveSev !== "UNKNOWN" ? (
                                                <Badge className={`${(cveConfig && cveConfig.bg) || "bg-gray-600"} ${(cveConfig && cveConfig.text) || "text-white"} text-xs`}>
                                                  {cveSev}
                                                </Badge>
                                              ) : (
                                                <Badge className="bg-amber-600/70 text-white text-xs" title="NVD hasn't scored this CVE yet">
                                                  Unscored
                                                </Badge>
                                              )}
                                            </td>
                                            <td className="text-right px-3 py-2 text-white/90">
                                              {typeof cve.base_score === "number" ? cve.base_score.toFixed(1) : "—"}
                                            </td>
                                            <td className="text-right px-3 py-2 text-white/90">{epssPct}</td>
                                            <td className="text-center px-3 py-2">
                                              {cve.kev ? (
                                                <Badge className="bg-red-700 text-white text-xs" title="Listed in CISA's Known Exploited Vulnerabilities catalog">
                                                  KEV
                                                </Badge>
                                              ) : (
                                                <span className="text-white/30 text-xs">—</span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-white/80">
                                              {cve.fixed_in || <span className="text-white/30">—</span>}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                {excludedVersions.length > 0 && (
                  <div className="mt-4 p-3 bg-orange-900/20 border border-orange-700 rounded-lg">
                    <div className="text-orange-300 text-sm">
                      <strong>{excludedVersions.length} version(s) excluded from upgrades:</strong>{" "}
                      {excludedVersions.join(", ")}
                    </div>
                    <div className="text-orange-400/70 text-xs mt-1">
                      Devices running these versions will not be added to deployment groups when new versions are deployed.
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* App-to-app dependencies and upgrade impact */}
            <FamilyDependenciesCard
              familyName={appData.family_name || familyName}
              getAccessToken={getAccessToken}
            />

            {/* Rationalised to another family / families rationalised into this one */}
            <FamilyRationalisationCard
              familyName={appData.family_name || familyName}
              getAccessToken={getAccessToken}
            />
          </div>

          {/* Right Column - Quick Stats */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Monitor className="h-5 w-5" />
                  Estate Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-[rgba(229,239,250,0.1)]">
                  <span className="text-white/90">Total Installs</span>
                  <span className="text-white font-semibold">{appData.unique_device_count?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[rgba(229,239,250,0.1)]">
                  <span className="text-white/90">Versions in Estate</span>
                  <span className="text-white font-semibold">{appData.version_count || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[rgba(229,239,250,0.1)]">
                  <span className="text-white/90">Latest Version</span>
                  <span className="text-white font-semibold">{appData.highest_version || "-"}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-white/90">Publisher</span>
                  <span className="text-white font-semibold">{appData.publisher || "-"}</span>
                </div>
              </CardContent>
            </Card>

            {/* Deployment Status */}
            <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Rocket className="h-5 w-5" />
                  Deployment Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  {getStatusBadge()}
                </div>
                {appData.package_info && (
                  <div className="mt-3 p-3 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-lg space-y-2">
                    <div className="text-sm font-medium text-white">Package Details</div>
                    <div className="text-sm text-white/90">
                      Name: <span className="text-white">{appData.package_info.name}</span>
                    </div>
                    {appData.package_info.version && (
                      <div className="text-sm text-white/90">
                        Version: <span className="text-white">{appData.package_info.version}</span>
                      </div>
                    )}
                    {appData.package_info.winget_id && (
                      <div className="text-sm text-white/90">
                        Winget: <span className="text-white font-mono text-xs">{appData.package_info.winget_id}</span>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 text-blue-400 border-blue-500 hover:bg-blue-900/20"
                      onClick={() => navigate(`/apps/${appData.package_info.package_id}`)}
                    >
                      <Package className="h-3 w-3 mr-2" />
                      View Package Details
                    </Button>
                  </div>
                )}
                {!appData.package_info && appData.packaging_status?.package_id && (
                  <div className="text-sm text-white/90">
                    Package ID: <span className="text-white/90 font-mono">{appData.packaging_status.package_id}</span>
                  </div>
                )}
                {appData.packaging_status?.intune_app_id && (
                  <div className="text-sm text-white/90">
                    Intune ID: <span className="text-white/90 font-mono text-xs">{appData.packaging_status.intune_app_id}</span>
                  </div>
                )}
                {appData.packaging_status?.synced_at && (
                  <div className="text-xs text-white/80 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last synced: {new Date(appData.packaging_status.synced_at).toLocaleString()}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Test Approval Status */}
            {appData.packaging_status?.package_id && (
              <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Test Approvals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {testApprovals && testApprovals.approvals?.length > 0 ? (
                    <>
                      {/* Summary */}
                      <div className="alice-kpi-row grid grid-cols-3 gap-2 mb-4 text-center">
                        <div className="bg-green-900/30 rounded-lg p-2">
                          <div className="text-xl font-bold text-green-400">{testApprovals.summary.approved}</div>
                          <div className="text-xs text-white/90">Approved</div>
                        </div>
                        <div className="bg-orange-900/30 rounded-lg p-2">
                          <div className="text-xl font-bold text-orange-400">{testApprovals.summary.pending}</div>
                          <div className="text-xs text-white/90">Pending</div>
                        </div>
                        <div className="bg-red-900/30 rounded-lg p-2">
                          <div className="text-xl font-bold text-red-400">{testApprovals.summary.rejected}</div>
                          <div className="text-xs text-white/90">Rejected</div>
                        </div>
                      </div>
                      
                      {/* Individual Responses */}
                      <div className="space-y-2">
                        {testApprovals.approvals.map((approval, idx) => (
                          <ApprovalRow key={idx} approval={approval} idx={idx} />
                        ))}
                      </div>
                      
                      {testApprovals.summary.approved === testApprovals.summary.total && testApprovals.summary.total > 0 && (
                        <div className="mt-3 p-2 bg-green-900/20 rounded-lg text-center">
                          <CheckCircle className="h-4 w-4 text-green-400 inline mr-2" />
                          <span className="text-green-400 text-sm font-medium">All testers approved - Ready for Go Live!</span>
                        </div>
                      )}
                      
                      {/* Resend button when there are pending approvals */}
                      {testApprovals.summary.pending > 0 && (
                        <div className="mt-3 text-center">
                          <Button
                            onClick={handleResendNotifications}
                            disabled={sendingNotifications}
                            variant="outline"
                            size="sm"
                            className="text-blue-400 border-blue-500 hover:bg-blue-900/20"
                            data-testid="resend-tester-notifications-btn"
                          >
                            {sendingNotifications ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Mail className="h-4 w-4 mr-2" />
                            )}
                            {sendingNotifications ? "Sending..." : "Resend Notifications"}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-white/80 text-sm">
                        {appData.packaging_status?.status === "deployed_test" && testers.length > 0 ? (
                          <>
                            <p>Deployed to test but no approval requests sent yet.</p>
                            <p className="text-xs mt-1 mb-3">Click below to send notification emails to testers.</p>
                            <Button
                              onClick={handleResendNotifications}
                              disabled={sendingNotifications}
                              className="bg-blue-600 hover:bg-blue-700"
                              size="sm"
                              data-testid="send-tester-notifications-btn"
                            >
                              {sendingNotifications ? (
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Mail className="h-4 w-4 mr-2" />
                              )}
                              {sendingNotifications ? "Sending..." : "Send Tester Notifications"}
                            </Button>
                          </>
                        ) : testers.length > 0 ? (
                          <>
                            <p>No test deployment yet.</p>
                            <p className="text-xs mt-1">Deploy to test group to request tester approvals.</p>
                          </>
                        ) : (
                          <>
                            <p>No testers configured.</p>
                            <p className="text-xs mt-1">Add testers above to enable approval workflow.</p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Security */}
            {appData.cve_data && (
              <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Security
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {appData.cve_data.has_cves ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-red-400">
                        <AlertTriangle className="h-4 w-4" />
                        {appData.cve_data.cve_count} known vulnerabilities
                      </div>
                      <div className="text-sm text-white/90">
                        {(() => {
                          const sev = appData.cve_data.highest_severity;
                          const valid = sev && !["NONE", "UNKNOWN"].includes(String(sev).toUpperCase());
                          return (
                            <>Highest severity: <span className="text-red-400 font-semibold">{valid ? String(sev).toUpperCase() : '—'}</span></>
                          );
                        })()}
                      </div>
                      
                      {/* CVE List with links */}
                      <div className="mt-4 space-y-2">
                        <p className="text-xs text-white/80 uppercase tracking-wider">Associated CVEs</p>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {appData.cve_data.cves?.map((cve, idx) => {
                            const severityColors = {
                              'CRITICAL': 'bg-red-600 text-white border-red-500',
                              'HIGH': 'bg-orange-600 text-white border-orange-400',
                              'MEDIUM': 'bg-yellow-600 text-white border-yellow-400',
                              'LOW': 'bg-blue-600 text-white border-blue-400'
                            };
                            const colorClass = severityColors[cve.base_severity] || 'bg-gray-600 text-white border-gray-400';
                            
                            return (
                              <a
                                key={idx}
                                href={`https://www.cve.org/CVERecord?id=${cve.cve_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between p-2 rounded bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] hover:bg-white/[0.04] transition-colors group"
                              >
                                <div className="flex items-center gap-2">
                                  <Badge className={`${colorClass} text-xs`}>
                                    {cve.base_severity}
                                  </Badge>
                                  <span className="text-white font-mono text-sm">{cve.cve_id}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-white/90 text-xs">Score: {cve.base_score?.toFixed(1)}</span>
                                  <ExternalLink className="h-3 w-3 text-white/80 group-hover:text-blue-400 transition-colors" />
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : appData.cve_data.evaluated === false ? (
                    <div className="space-y-2 text-white/80 text-sm">
                      <div className="flex items-center gap-2 text-white/70">
                        <Shield className="h-4 w-4" />
                        Not yet evaluated
                      </div>
                      <p className="text-xs text-white/60">
                        This family hasn't been mapped to a CPE on the Vulnerabilities tab yet, so version verdicts are unavailable. Assign a CPE under <span className="text-cyan-300">Apps Management → Discover → Vulnerabilities</span> to score it.
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="h-4 w-4" />
                      No known vulnerabilities
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Governance Metadata */}
            {appData.governance?.updated_at && (
              <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                <CardContent className="py-4">
                  <div className="text-xs text-white/80">
                    <div>Created: {appData.governance.created_at ? new Date(appData.governance.created_at).toLocaleString() : "Never"}</div>
                    <div>Updated: {new Date(appData.governance.updated_at).toLocaleString()}</div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppGovernancePage;
